import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LANDING } from "@/constants/testIds";

// Decorative autoplay clips. Replace the MP4s in frontend/public/videos/ to change content.
const CLIPS = [1, 2, 3, 4, 5, 6].map((n) => {
  const id = String(n).padStart(2, "0");
  return { id, src: `/videos/showcase-${id}.mp4`, poster: `/videos/showcase-${id}.jpg` };
});

function ShowcaseClip({ src, poster, id }: { src: string; poster: string; id: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = true;
    el.defaultMuted = true;
    const io = new IntersectionObserver(
      ([e]) => {
        const on = e.isIntersecting;
        setVisible(on);
        if (on) el.play().catch(() => undefined);
        else el.pause();
      },
      { rootMargin: "200px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (visible && el && el.paused) el.play().catch(() => undefined);
  }, [visible]);

  return (
    <div
      data-testid={`${LANDING.showcaseCard}-${id}`}
      aria-hidden
      className="glass relative aspect-[9/16] w-full select-none overflow-hidden rounded-2xl border-[#7FD4FF]/25 bg-[#070B14] shadow-[0_0_40px_-20px_rgba(127,212,255,0.45)]"
      style={{ cursor: "default" }}
    >
      <video
        ref={ref}
        data-testid={`${LANDING.showcaseVideo}-${id}`}
        className="pointer-events-none h-full w-full object-cover"
        src={visible ? src : undefined}
        poster={poster}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        disablePictureInPicture
        disableRemotePlayback
        controls={false}
        tabIndex={-1}
        onContextMenu={(e) => e.preventDefault()}
      />
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#070B14]/55 via-transparent to-transparent" />
    </div>
  );
}

export const ShowcaseVideos = () => {
  const { t } = useTranslation();
  return (
    <section data-testid={LANDING.showcase} className="relative mx-auto -mt-16 max-w-7xl px-6 pb-28 sm:px-10 lg:-mt-20">
      <div className="relative">
        <span className="inline-flex items-center gap-3 font-display text-[11px] font-semibold uppercase tracking-[0.36em] text-[#9CC8FF]">
          <span className="h-px w-10 bg-gradient-to-r from-[#7FD4FF] to-transparent shadow-[0_0_10px_#2E7BFF]" />
          {t("showcase.eyebrow")}
        </span>
        <h2 data-testid={LANDING.showcaseTitle} className="headline-shadow mt-5 font-display text-3xl font-bold uppercase leading-[0.95] tracking-[0.02em] text-foreground sm:text-4xl lg:text-5xl">
          {t("showcase.title")}
        </h2>
        <p className="mt-4 max-w-md text-base leading-relaxed text-[#B8C4DA] sm:text-lg">{t("showcase.subtitle")}</p>
      </div>
      <div data-testid={LANDING.showcaseGrid} className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
        {CLIPS.map((c) => <ShowcaseClip key={c.id} {...c} />)}
      </div>
    </section>
  );
};
